import { List, Map } from 'immutable';
import { createSelector, defaultMemoize } from 'reselect';
import { AppState } from 'app/store';
import { memoizeImmutableResult } from 'app/utils/redux';
import { getEntityOfType, getEntityOfTypeOrThrow, getAllEntitiesOfType } from 'app/models/entities/selectors';
import {
    Text,
    WordTranslation,
    Word,
    VocabSectionWord,
    LocalWord,
    CommonText,
    TextTranslation,
    WordSentence,
    VocabSectionWordSentence,
    LocalSentence,
    Sentence,
    SentenceTranslation,
    PdfSection,
    PdfTranslation,
 } from 'app/constants/schemas';
import { IWord } from 'app/constants/schemas/Word';
import { ILocalWord } from 'app/constants/schemas/LocalWord';
import {
  EntityStringId,
  Entity,
  IEntityType,
  IIdentifiableEntity,
  EntityFields,
  ExtractIEntity,
} from 'app/types/entity';
import { filterUndefined } from 'app/utils/immutable';
import { Language } from 'app/constants/enums/Language';

export type Translation = WordTranslation | TextTranslation | SentenceTranslation | PdfTranslation;
export interface ISingleTranslation {
  translation: string;
}
export interface IManyTranslations {
  translations: List<string>;
}
export interface IPdfTranslation {
  pdf: string;
}
export type Translatable = ISingleTranslation | IManyTranslations | IPdfTranslation;

const makeGetEntity = <T extends IIdentifiableEntity>(typeObject: IEntityType<T>) =>
  (state: AppState, id: EntityStringId): any =>
    getEntityOfType(state, typeObject, id);

const makeGetAllEntities = <T extends IIdentifiableEntity>(typeObject: IEntityType<T>) => (state: AppState) =>
  getAllEntitiesOfType(state, typeObject);

const makeGetTranslations = <P extends IEntityType<any>, C extends IEntityType<any>>(
  parentType: P,
  childType: C,
  field: P extends any ? keyof EntityFields<ExtractIEntity<P>> : never,
) => createSelector(
  [makeGetEntity(parentType), makeGetAllEntities<ExtractIEntity<C>>(childType)],
  memoizeImmutableResult(
    (entity: Entity<ExtractIEntity<P>>, allTranslations: Map<EntityStringId, Entity<ExtractIEntity<C>>>) => {
      let child: List<EntityStringId>;
      if (entity && entity[field]) {
        child = entity[field] as unknown as List<EntityStringId>;
      } else {
        child = List<EntityStringId>([]);
      }
      return filterUndefined(child.map(id => allTranslations.get(id)));
    },
  ),
);

export const makeGetTextTranslations = () =>
  makeGetTranslations(Text, TextTranslation, 'textTranslations');

export const makeGetCommonTextTranslations = () =>
  makeGetTranslations(CommonText, TextTranslation, 'textTranslations');

export const makeGetWordTranslationsWithType = (wordType: IEntityType<IWord> | IEntityType<ILocalWord>) =>
  makeGetTranslations(wordType, WordTranslation, 'translations');

export const makeGetWordTranslations = () => makeGetWordTranslationsWithType(Word);
export const makeGetLocalWordTranslations = () => makeGetWordTranslationsWithType(LocalWord);

/**
 * Creates a selector to get all translations from a vocab section word (Local or Global).
 * @returns translation selector - A selector to get vocab section word translations.
 */
export const makeGetVocabSectionWordTranslations = () => {
  const getWordTranslations = makeGetWordTranslations();
  const getLocalWordTranslations = makeGetLocalWordTranslations();
  return (state: AppState, id: EntityStringId) => {
    const vocabWord = getEntityOfType(state, VocabSectionWord, id);
    if (vocabWord) {
      return vocabWord.word === null ? getLocalWordTranslations(state, vocabWord.localWord) :
        getWordTranslations(state, vocabWord.word);
    } else {
      return List<WordTranslation>([]);
    }
  };
};

export const makeGetSentenceTranslations = () =>
  makeGetTranslations(Sentence, SentenceTranslation, 'translations');
export const makeGetLocalSentenceTranslations = () =>
  makeGetTranslations(LocalSentence, SentenceTranslation, 'translations');

export const makeGetWordSentenceTranslations = () => {
  const getSentenceTranslations = makeGetSentenceTranslations();
  return (state: AppState, id: EntityStringId) => {
    const wordSentence = getEntityOfTypeOrThrow(state, WordSentence, id);
    return getSentenceTranslations(state, wordSentence.sentence);
  };
};

export const makeGetVocabSectionWordSentenceTranslations = () => {
  const getSentenceTranslations = makeGetSentenceTranslations();
  const getLocalSentenceTranslations = makeGetLocalSentenceTranslations();
  const getWordSentenceTranslations = makeGetWordSentenceTranslations();
  return (state: AppState, id: EntityStringId) => {
    const vocabSectionSentence = getEntityOfType(state, VocabSectionWordSentence, id);
    if (vocabSectionSentence) {
      if (vocabSectionSentence.wordSentence !== null) {
        return getWordSentenceTranslations(state, vocabSectionSentence.wordSentence);
      } else if (vocabSectionSentence.localSentence !== null) {
        return getLocalSentenceTranslations(state, vocabSectionSentence.localSentence);
      } else if (vocabSectionSentence.globalSentence !== null) {
        return getSentenceTranslations(state, vocabSectionSentence.globalSentence);
      }
    }
    return List<SentenceTranslation>([]);
  };
};

export const makeGetPdfTranslations = () =>
  makeGetTranslations(PdfSection, PdfTranslation, 'pdfTranslations');

export const makeFilterTranslations = () =>
  defaultMemoize(
    <T extends Translation>(translations: List<T>, singleLanguageMode: boolean, displayLanguage: Language) =>
      translations.filter(({ language }) =>
        (singleLanguageMode ? language === displayLanguage : true),
      ),
  );

export const makeFindTranslation = () =>
  defaultMemoize(<T extends Translation>(translations: List<T>, showTranslations: boolean, displayLanguage: Language) =>
    translations.find(({ language }) =>
      showTranslations && language === displayLanguage,
    ),
  );
